import {css} from 'styled-components';

import {flexCenter} from '~/shared/theme/FlexLayout';
import {bodyBold, smallBold} from '~/shared/theme/typography';

// Base styles for all types of Pie buttons
const pieButtonBaseStyles = css<{isLoading: boolean; disabled?: boolean}>`
  ${flexCenter}
  color: ${({theme}) => theme.colors.charcoal80};
  cursor: pointer;
  border-radius: 100px;
  ${({theme, isLoading, disabled}) => !isLoading && disabled && `
    :disabled {
      background-color: ${theme.colors.actionDisabled};
      color: ${theme.colors.contentDisabled};
      cursor: no-drop;
    }
  `}
`;

const pieButtonSmall = css<{iconButton?: boolean}>`
  ${smallBold}
  padding: ${({iconButton}) => (iconButton ? '6px' : '6px 16px')};
`;

const pieButtonMedium = css<{iconButton?: boolean}>`
  ${smallBold}
  padding: ${({iconButton}) => (iconButton ? '8px' : '10px 16px')};
`;

const pieButtonLarge = css<{iconButton?: boolean}>`
  ${bodyBold}
  padding: ${({iconButton}) => (iconButton ? '10px' : '10px 16px')};
`;

const pieButtonXL = css`
  ${bodyBold}
  padding: 12px 16px;
`;

// Primary Pie Button Styles
// Base - do not export directly
const pieButtonPrimaryBaseStyles = css<{isLoading: boolean; disabled: boolean}>`
  ${pieButtonBaseStyles}
  color: ${({theme}) => theme.colors.contentWhite};
  background-color: ${({theme, isLoading}) => (isLoading ? theme.colors.actionBlackActive : theme.colors.actionBlack)};
  ${({theme, isLoading, disabled}) => !isLoading && !disabled && `
    :hover {
      background-color: ${theme.colors.actionBlackHover};
    }
  
    :active {
      background-color: ${theme.colors.actionBlackActive};
    }
  `}
`;

export const pieButtonPrimarySmallStyles = css`
  ${pieButtonPrimaryBaseStyles}
  ${pieButtonSmall}
`;

export const pieButtonPrimaryMediumStyles = css`
  ${pieButtonPrimaryBaseStyles}
  ${pieButtonMedium}
`;

export const pieButtonPrimaryLargeStyles = css`
  ${pieButtonPrimaryBaseStyles}
  ${pieButtonLarge}
`;

export const pieButtonPrimaryXLStyles = css`
  ${pieButtonPrimaryBaseStyles}
  ${pieButtonXL}
`;
// Primary end

// Brand Pie Button Styles
// Base - do not export directly
const pieButtonBrandBaseStyles = css<{isLoading: boolean; disabled: boolean}>`
  ${pieButtonBaseStyles}
  color: ${({theme}) => theme.colors.contentWhite};
  background-color: ${({theme, isLoading}) => (isLoading ? theme.colors.actionBrandActive : theme.colors.actionBrand)};
  ${({theme, isLoading, disabled}) => !isLoading && !disabled && `
    :hover {
      background-color: ${theme.colors.actionBrandHover};
    }
  
    :active {
      background-color: ${theme.colors.actionBrandActive};
    }
  `}
`;

export const pieButtonBrandSmallStyles = css`
  ${pieButtonBrandBaseStyles}
  ${pieButtonSmall}
`;

export const pieButtonBrandMediumStyles = css`
  ${pieButtonBrandBaseStyles}
  ${pieButtonMedium}
`;

export const pieButtonBrandLargeStyles = css`
  ${pieButtonBrandBaseStyles}
  ${pieButtonLarge}
`;
// Brand end

// Secondary Pie Button Styles
// Base - do not export directly
const pieButtonSecondaryBaseStyles = css<{isLoading: boolean; disabled: boolean}>`
  ${pieButtonBaseStyles}
  color: ${({theme}) => theme.colors.contentDefault};
  background-color: ${({theme, isLoading}) => (isLoading ? theme.colors.actionSecondaryActive : theme.colors.actionSecondary)};
  ${({theme, isLoading, disabled}) => !isLoading && !disabled && `
    :hover {
      background-color: ${theme.colors.actionSecondaryHover};
    }

    :active {
      background-color: ${theme.colors.actionSecondaryActive};
    }
  `}
`;

export const pieButtonSecondarySmallStyles = css`
  ${pieButtonSecondaryBaseStyles}
  ${pieButtonSmall}
`;

export const pieButtonSecondaryMediumStyles = css`
  ${pieButtonSecondaryBaseStyles}
  ${pieButtonMedium}
`;

export const pieButtonSecondaryLargeStyles = css`
  ${pieButtonSecondaryBaseStyles}
  ${pieButtonLarge}
`;

export const pieButtonSecondaryXLStyles = css`
  ${pieButtonSecondaryBaseStyles}
  ${pieButtonXL}
`;
// Secondary end

// Outline Pie Button Styles
// Base - do not export directly
const pieButtonOutlineBaseStyles = css<{isLoading: boolean; disabled: boolean}>`
  ${pieButtonBaseStyles}
  color: ${({theme}) => theme.colors.contentDefault};
  background-color: ${({theme, isLoading}) => (isLoading ? theme.colors.actionSecondaryActive : theme.colors.actionWhite)};
  border: 1px solid ${({theme}) => theme.colors.borderStrong};
  ${({theme, isLoading, disabled}) => !isLoading && !disabled && `
    :hover {
      background-color: ${theme.colors.actionWhiteHover};
    }
  
    :active {
      background-color: ${theme.colors.actionWhiteActive};
    }
  `}
`;

export const pieButtonOutlineSmallStyles = css`
  ${pieButtonOutlineBaseStyles}
  ${pieButtonSmall}
`;

export const pieButtonOutlineMediumStyles = css`
  ${pieButtonOutlineBaseStyles}
  ${pieButtonMedium}
`;

export const pieButtonOutlineLargeStyles = css`
  ${pieButtonOutlineBaseStyles}
  ${pieButtonLarge}
`;
// Outline end

// Ghost Pie Button Styles
// Base - do not export directly
const pieButtonGhostBaseStyles = css<{isLoading: boolean; disabled: boolean}>`
  ${pieButtonBaseStyles}
  color: ${({theme}) => theme.colors.contentDefault};
  background-color: ${({theme, isLoading}) => (isLoading ? theme.colors.actionWhiteActive : 'transparent')};
  ${({theme, isLoading, disabled}) => !isLoading && !disabled && `
    :hover {
      background-color: ${theme.colors.actionWhiteHover};
    }
  
    :active {
      background-color: ${theme.colors.actionWhiteActive};
    }
  `}
`;

export const pieButtonGhostSmallStyles = css`
  ${pieButtonGhostBaseStyles}
  ${pieButtonSmall}
`;

export const pieButtonGhostMediumStyles = css`
  ${pieButtonGhostBaseStyles}
  ${pieButtonMedium}
`;

export const pieButtonGhostLargeStyles = css`
  ${pieButtonGhostBaseStyles}
  ${pieButtonLarge}
`;
// Ghost end
